import styled from '@emotion/styled';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import pushRoute from '@utils/pushRoute';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { getIsLoggedIn } from '@api/selectors/auth';
import Typography from 'aquilla/core/atoms/Typography';
import { useEffect, useState } from 'react';
import RightChevron from 'aquilla/icons/actions/RightChevron';

const Container = styled.div`
  width: 100%;
  break-inside: avoid;
`;

const StateWrapper = styled.div`
  width: 100%;
  color: var(--color-i-gray-13);
  font-size: ${Fonts.H7};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_3B};
`;

const StateContainer = styled.div`
  width: 100%;
  margin-left: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_6};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_2};
    margin-left: 0px;
  }
`;

const SectionWrapper = styled.div`
  columns: 1;
  padding-bottom: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    padding-bottom: ${Spacings.SPACING_6B};
  }
`;

const ArrowContainer = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  margin-top: -${Spacings.SPACING_3B};
  height: ${Spacings.SPACING_6B};
  width: ${Spacings.SPACING_6B};
  margin-left: ${Spacings.SPACING_6};
  display: none;
`;

const CentreContainer = styled.div`
  cursor: pointer;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_2B} ${Spacings.SPACING_6}
    ${Spacings.SPACING_3B};
  &:hover {
    display: block;
    background-color: var(--color-i-violet-dark-1);
    border-radius: ${Spacings.SPACING_1B};
  }
  &:hover ${ArrowContainer} {
    display: block;
  }
  ${mediaQuery.uptoMobile} {
    padding: 0px;
    margin-top: ${Spacings.SPACING_3B};
  }
`;

const CentreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: ${Spacings.SPACING_2};
`;

const CityContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CityWrapper = styled.div`
  color: var(--color-i-black);
  font-size: ${Fonts.LARGE_2};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.LARGE};
  }
`;

const NewWrapper = styled.div`
  height: ${Spacings.SPACING_4B};
  width: ${Spacings.SPACING_10B};
  margin-top: ${Spacings.SPACING_2};
  margin-left: ${Spacings.SPACING_2B};
  background-color: var(--color-i-blue);
  border-radius: ${Spacings.SPACING_1B};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-i-white);
  font-size: ${Fonts.EXTRA_SMALL};
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_3B};
`;

const CentreItem = ({ data }) => {
  const { state, centres } = data;
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [clickedViewDetails, setClickedViewDetails] = useState(false);
  const [viewDetailsFlow, setViewDetailsFlow] = useState(false);
  const [selectedCentre, setSelectedCentre] = useState(null);
  const isHovered = true;

  const handleCentreRouting = () => {
    setClickedViewDetails(true);
    if (!isLoggedIn) {
      openLoginDrawer();
    } else {
      setViewDetailsFlow(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn && clickedViewDetails) setViewDetailsFlow(true);
  }, [clickedViewDetails, isLoggedIn]);

  useEffect(() => {
    if (viewDetailsFlow && isLoggedIn) {
      if (selectedCentre?.availableUids?.length >= 1) {
        const currGoalUID = selectedCentre?.availableUids[0];
        pushRoute({
          pathname: '/goal/-/[goalUID]/centre/-/[centreUID]',
          query: {
            goalUID: currGoalUID,
            centreUID: selectedCentre?.centreUid
          }
        });
      }
      setClickedViewDetails(false);
      setViewDetailsFlow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDetailsFlow]);

  return (
    <Container>
      <StateWrapper>
        <StateContainer>{state.toUpperCase()}</StateContainer>
        <section>
          <SectionWrapper>
            {centres.map((centre, index) => {
              return (
                <CentreContainer
                  onClick={() => {
                    setSelectedCentre(centre);
                    handleCentreRouting();
                  }}
                  key={`centre_item_${index}`}
                >
                  <CentreWrapper>
                    <CityContainer>
                      <CityWrapper>{centre.city}</CityWrapper>
                      {centre.isNew && <NewWrapper>NEW</NewWrapper>}
                    </CityContainer>
                    <Typography variant="p2" color="var(--color-i-gray-13)">
                      {centre.centreName}
                    </Typography>
                    {isHovered && (
                      <ArrowContainer>
                        <RightChevron color="var(--color-i-gray-13)" />
                      </ArrowContainer>
                    )}
                  </CentreWrapper>
                </CentreContainer>
              );
            })}
          </SectionWrapper>
        </section>
      </StateWrapper>
    </Container>
  );
};

export default CentreItem;
